import { ALL_TOOLS, type ChannelConfig, type TenantSettings, type ToolName } from '@lastrouter/contracts';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Copy,
  Dialog,
  DialogContent,
  ExternalLink,
  Field,
  Input,
  Label,
  Plus,
  Switch,
  Textarea,
  Trash2,
} from '@lastrouter/ui';
import { Link } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useApiQuery } from '../api/hooks.js';
import { AdminOnly } from '../app/shell.js';
import { EmptyState, KeyValue, linkClass, PageHeader, QueryState, StatusBadge } from '../components/common.js';
import { formatDateTime, splitList } from '../lib/format.js';
import { validLimit } from './limits.js';
import { connectorKind, effectiveChannels, fileChannels, productOf, removeChannel, saveChannel } from './products-logic.js';
import { useSaveSettings } from './settings-save.js';

const ID_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const RESERVED = ['default', 'cli', 'desconhecido'];

/** Ferramentas na ordem em que fazem sentido para quem lê; os nomes vêm do i18n, sem o id cru. */
const TOOL_ORDER: ToolName[] = ['list_apps', 'describe_app', 'query', 'list_values', 'run_validated_query', 'explain_result', 'report_feedback', 'propose_context_change', 'resolve_question', 'search_context', 'query_metric'];
const TOOLS = [...TOOL_ORDER.filter((x) => ALL_TOOLS.includes(x)), ...ALL_TOOLS.filter((x) => !TOOL_ORDER.includes(x))];

interface Ctx {
  settings: TenantSettings;
  file: ChannelConfig[];
  config: Record<string, unknown>;
}

/** Configurações e arquivo juntos: toda tela de produto lê os dois. */
function WithChannels({ children }: { children: (c: Ctx) => ReactNode }) {
  const settings = useApiQuery('settings');
  const system = useApiQuery('system');
  return (
    <QueryState query={settings}>
      {(s) => (
        <QueryState query={system}>{(sys) => children({ settings: s, file: fileChannels(sys.config), config: sys.config })}</QueryState>
      )}
    </QueryState>
  );
}

const chatChannelOf = (config: Record<string, unknown>) => ((config.chat as { channel?: string } | undefined)?.channel ?? 'web');
const publicUrlOf = (config: Record<string, unknown>) => (typeof config.public_url === 'string' ? config.public_url.replace(/\/$/, '') : window.location.origin);

/** De onde vêm os canais hoje, em uma frase, e o caminho de volta para o arquivo. */
function ChannelSource({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { save, pending } = useSaveSettings('products.savedFile');
  const [confirm, setConfirm] = useState(false);
  if (ctx.settings.channels === null) return <Alert tone="info" title={t('products.fromFile')} />;
  return (
    <div className="flex flex-wrap items-center gap-3 text-[length:var(--lr-text-sm)] text-fg-secondary">
      <span>{t('products.fromPanel')}</span>
      <Button size="sm" variant="link" className="h-auto px-0" onClick={() => setConfirm(true)}>
        {t('products.backToFile')}
      </Button>
      {confirm ? (
        <Dialog open onOpenChange={(o) => !o && setConfirm(false)}>
          <DialogContent title={t('products.backToFile')} description={t('products.backToFileHelp')} closeLabel={t('common.close')}>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setConfirm(false)}>{t('common.cancel')}</Button>
              <Button variant="danger" disabled={pending} onClick={() => save({ ...ctx.settings, channels: null }, () => setConfirm(false))}>
                {t('products.backToFile')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

/** Configurações do canal de um produto: ferramentas, limite, valores privados e, em Avançado, id e client ids. */
function ChannelCard({ ctx, original, title, fixedId, onCancel }: { ctx: Ctx; original: ChannelConfig | null; title: string; fixedId?: string; onCancel?: () => void }) {
  const { t } = useTranslation();
  const { save, pending } = useSaveSettings('products.saved');
  const init = () => ({
    id: original?.id ?? fixedId ?? '',
    tools: original?.tools ?? (['list_apps', 'describe_app', 'query', 'list_values', 'explain_result', 'report_feedback'] as ToolName[]),
    privateValues: original?.shows_private_values ?? false,
    clientIds: (original?.client_app_ids ?? []).join('\n'),
    limit: (original ? ctx.settings.limits.per_channel[original.id] : undefined)?.toString() ?? '',
  });
  const [d, setD] = useState(init);
  const [removing, setRemoving] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setD(init()), [ctx.settings.version]);

  const taken = effectiveChannels(ctx.settings, ctx.file).some((c) => c.id === d.id && c.id !== original?.id);
  const idError = !ID_RE.test(d.id) || RESERVED.includes(d.id) || taken ? t('products.idError') : undefined;
  const invalid = Boolean(idError) || d.tools.length === 0 || !validLimit(d.limit);
  const fallback = original?.calls_per_minute ?? ctx.settings.limits.calls_per_minute;

  function submit() {
    const edited: ChannelConfig = {
      ...(original ?? { staging: false }),
      id: d.id,
      client_app_ids: splitList(d.clientIds, /\n/),
      tools: TOOLS.filter((x) => d.tools.includes(x)),
      shows_private_values: d.privateValues,
    } as ChannelConfig;
    save(saveChannel(ctx.settings, ctx.file, original, edited, d.limit ? Number(d.limit) : null), onCancel);
  }

  const key = d.id || 'novo';
  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <CardTitle>{title}</CardTitle>
          <CardDescription>{t('products.channelHelp')}</CardDescription>
        </div>
        {original?.staging ? <StatusBadge tone="accent">{t('products.staging')}</StatusBadge> : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <fieldset>
          <legend className="mb-2 font-medium">{t('products.tools')}</legend>
          <p className="mb-3 text-[length:var(--lr-text-sm)] text-fg-secondary">{t('products.toolsHelp')}</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {TOOLS.map((tool) => (
              <label key={tool} className="flex items-start gap-2">
                <Checkbox className="mt-1" checked={d.tools.includes(tool)} onChange={(e) => setD({ ...d, tools: e.target.checked ? [...d.tools, tool] : d.tools.filter((x) => x !== tool) })} />
                <span>{t(`channels.toolNames.${tool}`)}</span>
              </label>
            ))}
          </div>
          {d.tools.length === 0 ? (
            <p role="alert" className="mt-1 text-[length:var(--lr-text-xs)] font-medium text-danger">
              {t('products.toolsRequired')}
            </p>
          ) : null}
        </fieldset>

        <Field
          id={`limite-${key}`}
          label={t('products.limit')}
          hint={fallback ? t('products.limitHelp', { n: fallback }) : t('products.limitHelpFile')}
          error={validLimit(d.limit) ? undefined : t('limits.invalid')}
          className="max-w-xs"
        >
          <Input inputMode="numeric" value={d.limit} onChange={(e) => setD({ ...d, limit: e.target.value.trim() })} placeholder={fallback?.toString()} />
        </Field>

        <div className="flex items-start gap-3 rounded-[var(--lr-radius-md)] border border-border p-3">
          <Switch id={`privados-${key}`} checked={d.privateValues} onCheckedChange={(v) => setD({ ...d, privateValues: v })} aria-describedby={`privados-${key}-ajuda`} />
          <div className="flex flex-col gap-0.5">
            <Label htmlFor={`privados-${key}`}>{t('products.privateValues')}</Label>
            <p id={`privados-${key}-ajuda`} className="text-[length:var(--lr-text-sm)] text-fg-secondary">
              {t('products.privateValuesHelp')}
            </p>
          </div>
        </div>

        <details open={!original && !fixedId} className="rounded-[var(--lr-radius-md)] border border-border p-3">
          <summary className="cursor-pointer font-medium">{t('products.advanced')}</summary>
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <Field id={`canal-id-${key}`} label={t('products.channelId')} hint={t('products.channelIdHelp')} error={idError}>
              <Input value={d.id} disabled={Boolean(fixedId)} onChange={(e) => setD({ ...d, id: e.target.value.trim() })} />
            </Field>
            <Field id={`canal-apps-${key}`} label={t('products.clientIds')} hint={t('products.clientIdsHelp')}>
              <Textarea rows={2} className="font-mono" value={d.clientIds} onChange={(e) => setD({ ...d, clientIds: e.target.value })} />
            </Field>
          </div>
        </details>

        <div className="flex flex-wrap gap-2">
          <Button variant="primary" disabled={invalid || pending} onClick={submit}>
            {t('common.save')}
          </Button>
          {onCancel ? <Button onClick={onCancel}>{t('common.cancel')}</Button> : null}
          {original && !fixedId ? (
            <Button variant="ghost" className="ml-auto" onClick={() => setRemoving(true)}>
              <Trash2 />
              {t('products.removeConnector')}
            </Button>
          ) : null}
        </div>
      </CardContent>
      {removing && original ? (
        <Dialog open onOpenChange={(o) => !o && setRemoving(false)}>
          <DialogContent title={t('products.removeConnector')} description={t('products.removeConnectorHelp', { name: title })} closeLabel={t('common.close')}>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setRemoving(false)}>{t('common.cancel')}</Button>
              <Button variant="danger" disabled={pending} onClick={() => save(removeChannel(ctx.settings, ctx.file, original.id), () => setRemoving(false))}>
                {t('products.removeConnector')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </Card>
  );
}

/** Canal fixo de um produto (Teams, Chat): mostra o canal se existe, ou o botão de criar. */
function FixedChannel({ ctx, id, title }: { ctx: Ctx; id: string; title: string }) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const current = effectiveChannels(ctx.settings, ctx.file).find((c) => c.id === id) ?? null;
  if (current || creating) return <ChannelCard ctx={ctx} original={current} title={title} fixedId={id} {...(current ? {} : { onCancel: () => setCreating(false) })} />;
  return (
    <Card>
      <CardContent className="flex flex-col items-start gap-3">
        <p className="text-fg-secondary">{t('products.noChannel', { id })}</p>
        <Button onClick={() => setCreating(true)}>
          <Plus />
          {t('products.createChannel')}
        </Button>
      </CardContent>
    </Card>
  );
}

function CopyField({ id, label, value }: { id: string; label: string; value: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        <Input id={id} readOnly value={value} className="font-mono" onFocus={(e) => e.target.select()} />
        <Button
          aria-label={t('products.copy')}
          onClick={() =>
            void navigator.clipboard.writeText(value).then(
              () => toast.success(t('products.copied')),
              () => toast.error(t('errors.generic')),
            )
          }
        >
          <Copy />
          <span className="max-sm:sr-only">{t('products.copy')}</span>
        </Button>
      </div>
    </div>
  );
}

function Steps({ items }: { items: string[] }) {
  return (
    <ol className="list-decimal space-y-1.5 pl-5">
      {items.map((s) => (
        <li key={s}>{s}</li>
      ))}
    </ol>
  );
}

// ---------- Ask no Teams ----------

export function TeamsProductPage() {
  const { t } = useTranslation();
  const last = useApiQuery('conversations', { query: { channel: 'teams', limit: 1 } });
  return (
    <AdminOnly>
      <PageHeader title={t('products.teams.title')} subtitle={t('products.teams.subtitle')} />
      <WithChannels>
        {(ctx) => {
          const configured = effectiveChannels(ctx.settings, ctx.file).some((c) => productOf(c.id, chatChannelOf(ctx.config)) === 'teams');
          const lastTurn = last.data?.items[0];
          return (
            <div className="flex flex-col gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t('products.status')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <KeyValue
                    items={[
                      {
                        label: t('products.teams.channel'),
                        value: configured ? <StatusBadge tone="success">{t('products.configured')}</StatusBadge> : <StatusBadge tone="warning">{t('products.notConfigured')}</StatusBadge>,
                      },
                      { label: t('products.teams.lastQuestion'), value: lastTurn ? formatDateTime(lastTurn.ts) : t('products.teams.noQuestion') },
                      { label: t('products.teams.package'), value: t('products.teams.packageHelp') },
                    ]}
                  />
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t('products.teams.installTitle')}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  <Steps items={[t('products.teams.install1'), t('products.teams.install2'), t('products.teams.install3')]} />
                </CardContent>
              </Card>
              <ChannelSource ctx={ctx} />
              <FixedChannel ctx={ctx} id="teams" title={t('products.teams.channelTitle')} />
            </div>
          );
        }}
      </WithChannels>
    </AdminOnly>
  );
}

// ---------- Chat ----------

export function ChatProductPage() {
  const { t } = useTranslation();
  return (
    <AdminOnly>
      <PageHeader
        title={t('products.chat.title')}
        subtitle={t('products.chat.subtitle')}
        actions={
          <Button asChild variant="primary">
            <a href="/chat" target="_blank" rel="noopener">
              <ExternalLink />
              {t('products.chat.open')}
            </a>
          </Button>
        }
      />
      <WithChannels>
        {(ctx) => {
          const on = Boolean(ctx.config.chat);
          const id = chatChannelOf(ctx.config);
          return (
            <div className="flex flex-col gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t('products.status')}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  <KeyValue
                    items={[
                      { label: t('products.chat.address'), value: <span className="font-mono">{`${publicUrlOf(ctx.config)}/chat`}</span> },
                      { label: t('products.chat.login'), value: on ? <StatusBadge tone="success">{t('products.configured')}</StatusBadge> : <StatusBadge tone="warning">{t('products.notConfigured')}</StatusBadge> },
                    ]}
                  />
                  <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{on ? t('products.chat.help') : t('products.chat.off')}</p>
                </CardContent>
              </Card>
              <ChannelSource ctx={ctx} />
              <FixedChannel ctx={ctx} id={id} title={t('products.chat.channelTitle')} />
            </div>
          );
        }}
      </WithChannels>
    </AdminOnly>
  );
}

// ---------- Conectores MCP ----------

export function McpProductPage() {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  return (
    <AdminOnly>
      <PageHeader
        title={t('products.mcp.title')}
        subtitle={t('products.mcp.subtitle')}
        actions={
          <Button variant="primary" onClick={() => setAdding(true)} disabled={adding}>
            <Plus />
            {t('products.mcp.add')}
          </Button>
        }
      />
      <WithChannels>
        {(ctx) => {
          const chat = chatChannelOf(ctx.config);
          const list = effectiveChannels(ctx.settings, ctx.file).filter((c) => productOf(c.id, chat) === 'mcp');
          return (
            <div className="flex flex-col gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t('products.mcp.connectTitle')}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <CopyField id="mcp-url" label={t('products.mcp.address')} value={`${publicUrlOf(ctx.config)}/mcp`} />
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <h3 className="font-medium">Claude</h3>
                      <Steps items={[t('products.mcp.claude1'), t('products.mcp.claude2'), t('products.mcp.claude3')]} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <h3 className="font-medium">Copilot Studio</h3>
                      <Steps items={[t('products.mcp.copilot1'), t('products.mcp.copilot2'), t('products.mcp.copilot3')]} />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <ChannelSource ctx={ctx} />
              {adding ? <ChannelCard ctx={ctx} original={null} title={t('products.mcp.newConnector')} onCancel={() => setAdding(false)} /> : null}
              {list.length === 0 && !adding ? <EmptyState>{t('products.mcp.empty')}</EmptyState> : null}
              {list.map((c) => (
                <ChannelCard key={c.id} ctx={ctx} original={c} title={t(`products.mcp.kinds.${connectorKind(c.id)}`, { id: c.id })} />
              ))}
            </div>
          );
        }}
      </WithChannels>
    </AdminOnly>
  );
}
